// src/sections/Media.js
import React, { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import './Media.css';

// ロゴ画像をインポート（正しいパスに修正してください）
import kodanshaLogo from '../assets/KODANSHA.jpg';
import kadokawaLogo from '../assets/KADOKAWA.jpg';
import futabashaLogo from '../assets/Futabasha.jpg';
import mitsubishiEstateLogo from '../assets/MitsubishiEstate.png';
import mitsuiFudosanLogo from '../assets/MitsuiFudosan.png';
import donQuijoteLogo from '../assets/DonQuijote.jpg';
import bushiroadLogo from '../assets/BUSHIROAD.png';
import neteaseLogo from '../assets/NETEAST.png';

const mediaPartners = [
  { id: 'kodansha', name: 'KODANSHA', logo: kodanshaLogo },
  { id: 'kadokawa', name: 'KADOKAWA', logo: kadokawaLogo },
  { id: 'futabasha', name: 'Futabasha', logo: futabashaLogo },
  { id: 'mitsubishiEstate', name: 'Mitsubishi Estate', logo: mitsubishiEstateLogo },
  { id: 'mitsuiFudosan', name: 'Mitsui Fudosan', logo: mitsuiFudosanLogo },
  { id: 'donQuijote', name: 'Don Quijote', logo: donQuijoteLogo },
  { id: 'bushiroad', name: 'BUSHIROAD', logo: bushiroadLogo },
  { id: 'netease', name: 'NETEASE', logo: neteaseLogo },
];

function Media() {
  const sectionRef = useRef(null);
  const logosContainerRef = useRef(null);
  const logosTrackRef = useRef(null);
  const scrollTween = useRef(null);

  const setupAutoScroll = () => {
    const container = logosContainerRef.current;
    const track = logosTrackRef.current;
    if (!container || !track) return;

    // 既存のアニメーションがあれば停止
    if (scrollTween.current) {
      scrollTween.current.kill();
      scrollTween.current = null;
    }

    const isMobile = window.matchMedia('(max-width: 768px)').matches;

    if (isMobile) {
      const originalLogos = Array.from(track.children).slice(0, mediaPartners.length);
      let originalContentWidth = 0;
      
      originalLogos.forEach(logoItem => {
        const computedStyle = getComputedStyle(logoItem);
        const width = logoItem.offsetWidth;
        const marginLeft = parseFloat(computedStyle.marginLeft || 0);
        const marginRight = parseFloat(computedStyle.marginRight || 0);
        originalContentWidth += width + marginLeft + marginRight;
      });

      scrollTween.current = gsap.to(track, {
        x: -originalContentWidth,
        duration: 30, 
        ease: 'none',
        repeat: -1,
        modifiers: {
          x: gsap.utils.unitize(x => parseFloat(x) % originalContentWidth)
        }
      });
    } else {
      gsap.set(track, { x: 0 });
    }
  };

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    // セクションのフェードインアニメーション
    const section = sectionRef.current;
    if (section) {
      gsap.fromTo(section,
        { autoAlpha: 0, y: 50 },
        {
          autoAlpha: 1,
          y: 0,
          duration: 1,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: section,
            start: 'top 80%',
            toggleActions: 'play none none none',
          }
        }
      );
    }

    const timer = setTimeout(() => {
      setupAutoScroll();
    }, 100);

    const handleResize = () => {
      clearTimeout(timer);
      setTimeout(setupAutoScroll, 100);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timer);
      if (scrollTween.current) {
        scrollTween.current.kill();
      }
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <section id="media" className="media-section" ref={sectionRef}>
      <h2 className="section-title">MEDIA</h2>
      <div className="media-logos-container" ref={logosContainerRef}>
        <div className="media-logos-track" ref={logosTrackRef}>
          {mediaPartners.map(partner => (
            <div key={partner.id} className="media-logo-item">
              <img src={partner.logo} alt={partner.name} className="media-logo-image" />
            </div>
          ))}
          {mediaPartners.map(partner => (
            <div key={`${partner.id}-clone`} className="media-logo-item">
              <img src={partner.logo} alt={partner.name} className="media-logo-image" />
            </div>
          ))}
          {mediaPartners.map(partner => (
            <div key={`${partner.id}-clone2`} className="media-logo-item">
              <img src={partner.logo} alt={partner.name} className="media-logo-image" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default Media;